import React, { useState, useEffect } from 'react';
import { 
  Users, Plus, X, Search, Shield, Settings, UserMinus, UserPlus, 
  Crown, AlertTriangle, Edit3, Trash2, CheckCircle 
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useChat } from '../../contexts/ChatContext';
import { useConfirm } from '../../contexts/ConfirmContext';

interface Member {
  id: string;
  name: string;
  role: 'admin' | 'member' | 'guest';
  avatar?: string;
  joinDate?: string;
}

interface AvailableMember {
  id: string;
  name: string;
  email?: string;
  avatar?: string;
  department?: string;
}

interface ChatMembersManagerProps {
  isOpen: boolean;
  onClose: () => void;
  members: Member[];
  onAddMember: (member: Member) => void;
  onRemoveMember: (memberId: string) => void;
  onUpdateMemberRole: (memberId: string, newRole: 'admin' | 'member' | 'guest') => void;
}

const ChatMembersManager: React.FC<ChatMembersManagerProps> = ({
  isOpen,
  onClose,
  members,
  onAddMember,
  onRemoveMember,
  onUpdateMemberRole
}) => {
  const { organization } = useOrganization();
  const { currentRoom, isCurrentUserAdmin } = useChat();
  const [activeTab, setActiveTab] = useState<'members' | 'add'>('members');
  const [searchTerm, setSearchTerm] = useState('');
  const [availableMembers, setAvailableMembers] = useState<AvailableMember[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const { confirm } = useConfirm();

  // Verificar se o usuário atual é administrador da sala
  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const adminStatus = await isCurrentUserAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Erro ao verificar status de admin:', error);
        setIsAdmin(false);
      }
    };

    if (currentRoom && isOpen) {
      checkAdminStatus();
    }
  }, [currentRoom, isCurrentUserAdmin, isOpen]);

  // Buscar membros disponíveis para adicionar
  useEffect(() => {
    const fetchAvailableMembers = async () => {
      if (!organization?.id || !isAdmin || !isOpen) return;

      try {
        setIsLoading(true);
        
        // Buscar membros da organização que não estão no chat
        const { data: orgMembers, error: orgError } = await supabase
          .from('organization_members')
          .select(`
            user_auth_id,
            role,
            user_profile!inner(
              id,
              name,
              user_photo,
              email
            )
          `)
          .eq('organization_id', organization.id);

        if (orgError) {
          console.error('Erro ao buscar membros da organização:', orgError);
          throw orgError;
        }

        if (orgMembers && orgMembers.length > 0) {
          const formattedMembers: AvailableMember[] = [];
          
          for (const member of orgMembers) {
            if (!member.user_profile || Array.isArray(member.user_profile)) continue;
            
            // Verificar se já é membro do chat
            const isAlreadyMember = members.find(m => m.id === member.user_auth_id);
            if (isAlreadyMember) continue;
            
            const profile = member.user_profile as any;
            formattedMembers.push({
              id: member.user_auth_id,
              name: profile.name || 'Usuário sem nome',
              email: profile.email || undefined,
              avatar: profile.user_photo || undefined,
              department: member.role || 'Membro'
            });
          }
          
          setAvailableMembers(formattedMembers);
        }
        
        setIsLoading(false);
      } catch (err) {
        console.error('Erro ao buscar membros:', err);
        setIsLoading(false);
      }
    };

    fetchAvailableMembers();
  }, [organization?.id, isAdmin, members, isOpen]);

  const filteredMembers = members.filter(member => 
    searchTerm === '' || 
    member.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAvailableMembers = availableMembers.filter(member => 
    searchTerm === '' || 
    member.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (member.email && member.email.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddMember = (member: AvailableMember) => {
    const newMember: Member = {
      id: member.id,
      name: member.name,
      role: 'member',
      avatar: member.avatar
    };
    
    onAddMember(newMember);
    setSearchTerm('');
  };

  const handleRemoveMember = async (memberId: string) => {
    const member = members.find(m => m.id === memberId);
    if (!member) return;

    const ok = await confirm({
      message: `Tem certeza que deseja remover ${member.name} da sala?`,
      variant: 'danger',
    });
    if (ok) {
      onRemoveMember(memberId);
    }
  };

  const handleRoleChange = async (newRole: 'admin' | 'member' | 'guest') => {
    if (!selectedMember) return;

    const roleNames = {
      admin: 'Administrador',
      member: 'Membro',
      guest: 'Convidado'
    };

    const ok = await confirm({
      message: `Tem certeza que deseja alterar o papel de ${selectedMember.name} para ${roleNames[newRole]}?`,
      variant: 'default',
    });
    if (ok) {
      onUpdateMemberRole(selectedMember.id, newRole);
      setSelectedMember(null);
      setShowRoleModal(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'admin':
        return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'member':
        return <Users className="w-4 h-4 text-coral" />;
      case 'guest':
        return <UserPlus className="w-4 h-4 text-gray-500" />;
      default:
        return <Users className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleName = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'member':
        return 'Membro';
      case 'guest':
        return 'Convidado';
      default:
        return 'Membro';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <Users className="w-6 h-6 text-coral" />
            <h2 className="text-xl font-semibold">Gerenciar Membros</h2>
            {isAdmin && (
              <div title="Você é administrador desta sala">
                <Shield className="w-5 h-5 text-coral" />
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('members')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'members'
                ? 'text-coral border-b-2 border-coral'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Membros ({members.length})
          </button>
          {isAdmin && (
            <button
              onClick={() => setActiveTab('add')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'add'
                  ? 'text-coral border-b-2 border-coral'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Adicionar Membros
            </button>
          )}
        </div>

        {/* Search */}
        <div className="p-6 border-b">
          <div className="relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={activeTab === 'members' ? 'Buscar membros...' : 'Buscar pessoas para adicionar...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-coral"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'members' ? (
            <div className="space-y-3">
              {filteredMembers.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Nenhum membro encontrado' : 'Nenhum membro no chat'}
                </p>
              ) : (
                filteredMembers.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.name}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <span className="text-lg font-medium">
                            {member.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-lg">{member.name}</div>
                        <div className="flex items-center gap-2 text-sm text-gray-500">
                          {getRoleIcon(member.role)}
                          <span>{getRoleName(member.role)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {isAdmin && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedMember(member);
                            setShowRoleModal(true);
                          }}
                          className="p-2 hover:bg-gray-200 rounded-full"
                          title="Alterar papel"
                        >
                          <Edit3 className="w-4 h-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleRemoveMember(member.id)}
                          className="p-2 hover:bg-gray-200 rounded-full"
                          title="Remover membro"
                        >
                          <Trash2 className="w-4 h-4 text-nk-error" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {isLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
                </div>
              ) : filteredAvailableMembers.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  {searchTerm ? 'Nenhuma pessoa encontrada' : 'Todas as pessoas já foram adicionadas'}
                </p>
              ) : (
                filteredAvailableMembers.map(member => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.name}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <span className="text-lg font-medium">
                            {member.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-lg">{member.name}</div>
                        <div className="text-sm text-gray-500">
                          {member.email || member.department}
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleAddMember(member)}
                      className="btn-primary"
                    >
                      <UserPlus className="w-4 h-4" />
                      Adicionar
                    </button>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Aviso para não administradores */}
        {!isAdmin && (
          <div className="p-6 border-t bg-yellow-50">
            <div className="flex items-center gap-2 text-nk-warn">
              <AlertTriangle className="w-5 h-5" />
              <span>
                Apenas administradores podem gerenciar membros do grupo.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Modal para alterar papel do membro */}
      {showRoleModal && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg p-6 w-96 max-w-90vw">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Alterar Papel</h3>
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                }}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Alterar o papel de <strong>{selectedMember.name}</strong>:
              </p>
              
              <div className="space-y-3">
                {(['admin', 'member', 'guest'] as const).map(role => (
                  <button
                    key={role}
                    onClick={() => handleRoleChange(role)}
                    className={`w-full text-left p-4 rounded-md border-2 transition-colors ${
                      selectedMember.role === role
                        ? 'border-coral bg-coral-soft'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {getRoleIcon(role)}
                      <div>
                        <div className="font-medium">{getRoleName(role)}</div>
                        <div className="text-sm text-gray-500">
                          {role === 'admin' && 'Pode gerenciar membros e configurações'}
                          {role === 'member' && 'Pode participar das conversas'}
                          {role === 'guest' && 'Acesso limitado às conversas'}
                        </div>
                      </div>
                      {selectedMember.role === role && (
                        <CheckCircle className="w-5 h-5 text-coral ml-auto" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedMember(null);
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMembersManager; 